import type { ButtonHTMLAttributes, ReactElement } from 'react';

import { cn } from '../lib/cn.js';

import './controls.css';

// The pill a screen's action uses (Cartel look): teal outline by default — teal
// is an accent, never a fill — ink for a secondary action, clay for money owed
// or attention, amber for "dar de baja". `solid` fills the tone (a confirm
// button), `bare` drops the border (the quieter second action), `soft` is a
// borderless parchment pill for actions that sit on a tinted band (the session
// panel's header), its text in the tone's colour, `wide` fills the row, `raised` is the parchment pill that sits on a dark strip.
//
// `quiet` is a sand outline and muted text, for an answer that asks for nothing
// («Vale»).
//
// `linkHover` gives a teal action the hover of a text link: only the colour deepens,
// with no pill behind it. For a bare action sitting inside another control.
//
// `on-ink` sits on the dark Nuberu panel: a parchment outline, `solid` a
// parchment pill with ink text, `bare` the muted second action («Ahora no»).
//
// One recipe: use `actionButtonClass` when the element isn't a plain <button>
// (a link, a router <Link>) or already carries other classes.

export type ActionTone = 'teal' | 'ink' | 'clay' | 'amber' | 'raised' | 'quiet' | 'on-ink';

// `xs` is the row action: the single quiet pill a list row may carry.
// `row` is the action of a taller row, 40 px and 44 px on a phone.
export type ActionSize = 'xs' | 'sm' | 'row' | 'md' | 'lg' | 'icon';

export interface ActionButtonStyle {
  readonly tone?: ActionTone;
  readonly size?: ActionSize;
  readonly solid?: boolean;
  readonly bare?: boolean;
  readonly soft?: boolean;
  readonly wide?: boolean;
  readonly linkHover?: boolean;
}

export const actionButtonClass = ({
  tone = 'teal',
  size = 'md',
  solid = false,
  bare = false,
  soft = false,
  wide = false,
  linkHover = false,
}: ActionButtonStyle = {}): string =>
  cn(
    'nu-btn',
    tone !== 'teal' && `nu-btn--${tone}`,
    size !== 'md' && `nu-btn--${size}`,
    solid && 'nu-btn--solid',
    bare && 'nu-btn--bare',
    soft && 'nu-btn--soft',
    wide && 'nu-btn--wide',
    linkHover && 'nu-btn--link-hover',
  );

export interface ActionButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'>, ActionButtonStyle {
  readonly type?: 'button' | 'submit';
}

export const ActionButton = ({
  tone,
  size,
  solid,
  bare,
  soft,
  wide,
  linkHover,
  type = 'button',
  className,
  ...rest
}: ActionButtonProps): ReactElement => (
  <button
    type={type}
    className={cn(
      actionButtonClass({
        ...(tone !== undefined ? { tone } : {}),
        ...(size !== undefined ? { size } : {}),
        ...(solid !== undefined ? { solid } : {}),
        ...(bare !== undefined ? { bare } : {}),
        ...(soft !== undefined ? { soft } : {}),
        ...(wide !== undefined ? { wide } : {}),
        ...(linkHover !== undefined ? { linkHover } : {}),
      }),
      className,
    )}
    {...rest}
  />
);
